import React, { Suspense } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CreateMeetingFormData } from "./types";
import { useVideoPlatformApi } from "./hooks/useVideoPlatformApi";
import { useGlobalLoading } from "../../hooks/useGlobalLoading";
import {
  CreateMeetingForm,
  isCreateMeetingFormValid,
  getDefaultCreateMeetingFormData,
  prepareCreateMeetingSubmitData,
} from "./CreateMeetingForm";

interface NewMeetingContentProps {
  onBack: () => void;
}

const NewMeetingContent: React.FC<NewMeetingContentProps> = ({ onBack }) => {
  const api = useVideoPlatformApi();
  const { withLoading } = useGlobalLoading();

  const [formData, setFormData] = React.useState<CreateMeetingFormData>(
    getDefaultCreateMeetingFormData()
  );
  const [enableMaxDuration, setEnableMaxDuration] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const handleSubmit = async () => {
    const submitData = prepareCreateMeetingSubmitData(formData, enableMaxDuration);

    try {
      await withLoading(api.createMeeting(submitData));
      setSnackbar({
        open: true,
        message: "Meeting created successfully",
        severity: "success",
      });
      // Navigate back after success
      setTimeout(() => {
        onBack();
      }, 1500);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create meeting";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center" }}>
        <IconButton
          onClick={onBack}
          color="primary"
          sx={{ border: "1px solid rgba(0, 0, 0, 0.12)" }}
          title="Back to Video Platform"
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>

      <Paper sx={{ p: 3 }}>
        <CreateMeetingForm
          formData={formData}
          onChange={setFormData}
          enableMaxDuration={enableMaxDuration}
          onEnableMaxDurationChange={setEnableMaxDuration}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button onClick={onBack}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!isCreateMeetingFormValid(formData)}
            sx={{
              backgroundColor: "#7357FF",
              "&:hover": { backgroundColor: "#5a42cc" },
            }}
          >
            Create Meeting
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

interface NewMeetingPageProps {
  onBack?: () => void;
}

export const NewMeetingPage: React.FC<NewMeetingPageProps> = ({ onBack }) => {
  const defaultOnBack = () => {
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.setItem("activeScreenId", "videoPlatform");
      window.location.reload();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Create New Meeting
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Set up a new video platform meeting
      </Typography>
      <Box sx={{ mt: 3 }}>
        <Suspense
          fallback={
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 400,
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <NewMeetingContent onBack={onBack || defaultOnBack} />
        </Suspense>
      </Box>
    </Box>
  );
};
